import React, { useState, useEffect } from 'react'

import Loader from './shared/Loader'
import Copyrights from './shared/Copyrights'
import LanguagePicker from './shared/LanguagePicker'
import { NO_IMAGE_SOURCE } from './shared/GlobalConstants'

export default function ChooseAccount({ appProps, screenClass, retrieveTenantsPath }) {
  const [loading, setLoading] = useState(false)
  const [tenants, setTenants] = useState([])
  const visualMode = appProps.mode === 'dark' ? 'dark' : 'light'

  useEffect(()=> {
    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: retrieveTenantsPath,
      dataType: 'json',
      success: res => {
        setTenants(res.tenants)
        setLoading(false)
      }
    })
  }, [])

  function handleClick(tenant) {
    if (tenant.is_admin) {
      window.location.href = `${tenant.tenant_url}/admin`
    } else {
      window.location.href = tenant.tenant_url
    }
  }

  function accountTitle(tenant) {
    return tenant.application_name && tenant.application_name.length > 0 ? tenant.application_name : tenant.tenant_url
  }

  function accessTypes(tenant) {
    return [tenant.is_user ? Cas.translations.tooltip_customer : null, tenant.is_admin ? Cas.translations.tooltip_admin : null].filter(t => t).join(', ')
  }

  function renderAccountTile(tenant, index) {
    return (
      <button key={ index } type='button' className='choose-account-card' onClick={ ()=> handleClick(tenant) }>
        <div className='choose-account-card-glow'></div>
        <div className='choose-account-logo'>
          <img className='logotype' src={ tenant.logo ? tenant.logo : NO_IMAGE_SOURCE } alt='' />
        </div>
        <div className='choose-account-card-content'>
          <div className='choose-account-card-kicker'>{ Cas.translations.choose_account_destination }</div>
          <h3>{ accountTitle(tenant) }</h3>
          { tenant.is_admin ?
            <div className='choose-account-access'>
              <span>{ Cas.translations.your_access }</span>
              <strong>{ accessTypes(tenant) }</strong>
            </div>
            :
            <div className='choose-account-access subtle'>
              <span>{ Cas.translations.choose_account_standard_access }</span>
              <strong>{ Cas.translations.tooltip_customer }</strong>
            </div>
          }
        </div>
        <div className='choose-account-card-action'>
          <span>{ Cas.translations.choose_account_enter_action }</span>
          <i className='fa-light fa-arrow-right'></i>
        </div>
      </button>
    )
  }

  return (
    <div className={ `wide-wrapper ${screenClass || 'account-screen choose-account-page'}` }>
      <main className='wide-left choose-account-main'>
        <div className='choose-account-shell'>
          <section className='create-instance-hero choose-account-hero'>
            <div className='create-instance-hero-copy'>
              <img className='choose-account-brand' src={ appProps.logo } alt={ appProps.instanceName } />
              <div className='eyebrow'>{ Cas.translations.choose_account_eyebrow }</div>
              <h1>{ Cas.translations.login_title }</h1>
              <p>{ tenants && tenants.length > 1 ? Cas.translations.choose_account_more : Cas.translations.choose_account }</p>
              <LanguagePicker className='choose-account-language-picker' />
            </div>
            <div className='choose-account-hero-panel'>
              <div className='hero-stat'>
                <span>{ loading ? '...' : tenants.length }</span>
                <div>{ Cas.translations.choose_account_available_accounts }</div>
              </div>
              <div className='hero-stat'>
                <span><i className='fa-light fa-shield-check'></i></span>
                <div>{ Cas.translations.choose_account_secure_session }</div>
              </div>
            </div>
          </section>

          <section className='create-instance-flow choose-account-flow' aria-label={ Cas.translations.choose_account_flow_label }>
            <div className='flow-step active'>
              <i className='fa-light fa-user-check'></i>
              <span>{ Cas.translations.choose_account_identify_step }</span>
            </div>
            <div className='flow-line'></div>
            <div className='flow-step active'>
              <i className='fa-light fa-grid-2'></i>
              <span>{ Cas.translations.choose_account_select_step }</span>
            </div>
            <div className='flow-line'></div>
            <div className='flow-step'>
              <i className='fa-light fa-arrow-right-to-bracket'></i>
              <span>{ Cas.translations.choose_account_enter_step }</span>
            </div>
          </section>

          <section className='create-instance-wrapper choose-account-panel'>
            <div className='section-heading'>
              <div>
                <div className='eyebrow'>{ Cas.translations.choose_account_panel_eyebrow }</div>
                <h2>{ Cas.translations.choose_account_panel_title }</h2>
              </div>
              <div className='availability-pill'>
                <i className='fa-light fa-building-user'></i>
                <span>{ tenants.length }</span>
              </div>
            </div>

            { loading ?
              <div className='choose-account-loading'>
                <Loader size='medium' position='centered'/>
                <span>{ Cas.translations.choose_account_loading_accounts }</span>
              </div>
              :
              <>
                { tenants.length > 0 ?
                  <div className='choose-account-grid'>
                    { tenants.map((tenant, index) => renderAccountTile(tenant, index)) }
                  </div>
                  :
                  <div className='choose-account-empty'>
                    <i className='fa-light fa-circle-info'></i>
                    <div>
                      <h2>{ Cas.translations.choose_account_empty_title }</h2>
                      <p>{ Cas.translations.choose_account_empty_description }</p>
                    </div>
                  </div>
                }
              </>
            }

            <div className='choose-account-profile-action'>
              <div>
                <span>{ Cas.translations.choose_account_profile_eyebrow }</span>
                <strong>{ Cas.translations.choose_account_profile_title }</strong>
              </div>
              <a className='button inverted' href={ appProps.profilePath }>
                <i className='fa-light fa-user-gear'></i>
                { Cas.translations.or_visit_account }
              </a>
            </div>
          </section>

          <Copyrights appProps={ appProps } />
        </div>
      </main>

      <aside className={ `wide-image choose-account-visual mode-${visualMode}` }>
        <div className='sso-info'>
          <div className='sso-icon'>
            <i className='fa-light fa-key'></i>
          </div>
          <div className='sso-text'>{ appProps.instanceName }</div>
          <p>{ Cas.translations.choose_account_visual_caption }</p>
        </div>
      </aside>
    </div>
  )
}
